import React, { FunctionComponent } from 'react'
import { FormattedMessage } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { RECORDING_CONSENT_ZENDESK_ARTICLE_URL } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { BulletedList } from '../../atoms/bulletedList/BulletedList'
import { ChevronIconDirection } from '../../atoms/icons/ChevronIcon'
import { ChevronV2Icon } from '../../atoms/icons/ChevronV2Icon'
import { InfoPopover } from '../../atoms/infoPopover/InfoPopover'
import { Link } from '../../atoms/link/Link'
import { BodyTextSize } from '../../styles/typeStyles'
import { tID } from '../../utils/utils'

const Container = styled.Pressable(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['4px'],
}))

export type RecordingConsentInfoProps = {
  isPopoverOpen?: boolean
  setIsPopoverOpen?: (isOpen: boolean) => void
}

export const RecordingConsentInfo: FunctionComponent<RecordingConsentInfoProps> = ({
  isPopoverOpen,
  setIsPopoverOpen,
}) => {
  const { colors } = useTheme()
  return (
    <InfoPopover
      header={
        <FormattedMessage defaultMessage='Let your client know:' description='Header for recording consent info' />
      }
      content={
        <BulletedList
          size={BodyTextSize.SMALL}
          spaceBetween={4}
          data={[
            {
              value: (
                <FormattedMessage
                  defaultMessage='Secure recordings create accurate session summaries and ensure the highest quality of care.'
                  description='Bullet point that explains how secure recordings create accurate session summaries and ensure the highest quality of care'
                />
              ),
            },
            {
              value: (
                <FormattedMessage
                  defaultMessage='Access is restricted to a select group of internal team members bound by strict confidentiality.'
                  description='Bullet point that explains how access is restricted to a select group of internal team members bound by strict confidentiality'
                />
              ),
            },
            {
              value: (
                <FormattedMessage
                  defaultMessage='Automatic deletion occurs after 2 weeks to protect your privacy.'
                  description='Bullet point that explains how automatic deletion occurs after 2 weeks to protect your privacy'
                />
              ),
            },
            {
              value: (
                <FormattedMessage
                  defaultMessage='De-identified content (all personal info removed) may be used to improve services and train AI tools.'
                  description='Bullet point that explains how de-identified content (all personal info removed) may be used to improve services and train AI tools'
                />
              ),
            },
            {
              value: (
                <FormattedMessage
                  defaultMessage='Participation is voluntary - you can decline or change your mind at any time without affecting your care.'
                  description='Bullet point that explains how participation is voluntary - you can decline or change your mind at any time without affecting your care'
                />
              ),
            },
          ]}
        />
      }
      footerContent={
        <BodyText
          size={BodyTextSize.SMALL}
          text={
            <FormattedMessage
              defaultMessage='See <supportLink>this article</supportLink> for further guidance.'
              description='Text offer providers information about connecting their calendar as well as support if they need more help'
              values={{
                supportLink: (text: string) => (
                  <Link
                    testID={tID('RecordingConsentInfo-support-link')}
                    text={text}
                    onPress={() => window.open(RECORDING_CONSENT_ZENDESK_ARTICLE_URL, '_blank')}
                    size={BodyTextSize.SMALL}
                    underline
                  />
                ),
              }}
            />
          }
        />
      }
      placement='bottom left'
      popoverTriggerAction='press'
      offset={8}
      isPopoverOpen={isPopoverOpen}
      setIsPopoverOpen={setIsPopoverOpen}
      trigger={
        <Container testID={tID('RecordingConsentInfo-what-to-mention-button')}>
          <BodyText
            text={
              <FormattedMessage
                defaultMessage='What to mention'
                description='Link text to learn more about recording consent'
              />
            }
            color={colors.linkDefault}
          />
          <ChevronV2Icon direction={ChevronIconDirection.DOWN} fillColor={colors.iconActive} />
        </Container>
      }
    />
  )
}
